import { gql, useMutation } from "@apollo/client";
import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../../core/auth";
import { identify, Identify, track } from "@amplitude/analytics-browser";
import BackArrow from "../../components/common/back-arrow";
import FadeInWrapper from "../../components/common/fade-in-wrapper";
import { notifyError } from "../../utils/toast";
import { getErrorMessage } from "../../utils/error";

const PURE_SIGNUP = gql`
  mutation PureSignup(
    $email: String!
    $password: String!
    $push_token: String
    $uuid: String
  ) {
    pureSignup(
      email: $email
      password: $password
      push_token: $push_token
      uuid: $uuid
    ) {
      user {
        id
        email
        phone
        createdAt
        updatedAt
      }
      token
    }
  }
`;

const SignUp: React.FC = () => {
  const { login: authLogin } = useAuth();
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [passwordCheck, setPasswordCheck] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [privacyPolicyChecked, setPrivacyPolicyChecked] = useState(false);
  const [termsChecked, setTermsChecked] = useState(false);
  const [passwordCheckError, setPasswordCheckError] = useState("");
  const [termsError, setTermsError] = useState("");
  const [pureSignup] = useMutation(PURE_SIGNUP, {
    onCompleted: (data) => {
      setIsSubmitting(false);
      const jwt = data.pureSignup.token;
      if (jwt) {
        const updateUserPropertiesAfterSignup = () => {
          const identifyUser = new Identify().set("isRegisteredUser", true);
          identify(identifyUser);
        };
        updateUserPropertiesAfterSignup();
        localStorage.setItem("jwt", jwt);
        track("이메일 회원가입 성공");
        authLogin();
        navigate("/phone-number-auth");
      }
    },
    onError: (error) => {
      setIsSubmitting(false);
      notifyError(getErrorMessage(error));
    },
  });

  const handleSignUp = (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);

    // Check if passwords match
    if (password !== passwordCheck) {
      setPasswordCheckError("비밀번호가 일치하지 않습니다.");
      setTermsError("");
      setIsSubmitting(false);
      return;
    }
    setPasswordCheckError("");

    // Check if both checkboxes are checked
    if (!privacyPolicyChecked || !termsChecked) {
      setTermsError("개인정보 처리방침과 서비스 이용약관에 모두 동의해 주세요.");
      setIsSubmitting(false);
      return;
    }
    setTermsError("");

    const push_token = localStorage.getItem("expo_push_token") as string | null;

    pureSignup({
      variables: {
        email,
        password,
        push_token,
        uuid: localStorage.getItem("mahi_uuid"),
      },
    });
  };

  return (
    <div className="flex items-center justify-center min-h-screen p-4">
      <FadeInWrapper className="w-full max-w-md bg-white p-6 pb-10">
        <div className="mb-4" onClick={() => navigate(-1)}>
          <BackArrow />
        </div>
        {/* Header */}
        <h2 className="text-2xl font-semibold text-center text-black mb-6">
          회원가입
        </h2>

        {/* Sign-Up Form */}
        <form onSubmit={handleSignUp} className="space-y-4">
          <div>
            <label
              htmlFor="email"
              className="block text-sm font-medium text-black mb-2 pl-2"
            >
              이메일
            </label>
            <input
              type="email"
              id="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              required
              className="w-full px-4 py-4 border border-gray-300 rounded-xl focus:outline-none focus:border-primary"
              placeholder="이메일을 입력하세요."
            />
          </div>

          <div>
            <label
              htmlFor="password"
              className="block text-sm font-medium text-black mb-2 pl-2"
            >
              비밀번호
            </label>
            <input
              type="password"
              id="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              required
              className="w-full px-4 py-4 border border-gray-300 rounded-xl focus:outline-none focus:border-primary"
              placeholder="비밀번호를 입력하세요."
            />
          </div>

          <div>
            <label
              htmlFor="passwordCheck"
              className="block text-sm font-medium text-black mb-2 pl-2"
            >
              비밀번호 확인
            </label>
            <input
              type="password"
              id="passwordCheck"
              value={passwordCheck}
              onChange={(e) => {
                setPasswordCheck(e.target.value);
                if (passwordCheckError) setPasswordCheckError("");
              }}
              required
              className="w-full px-4 py-4 border border-gray-300 rounded-xl focus:outline-none focus:border-primary"
              placeholder="동일하게 입력해주세요."
            />
            {passwordCheckError && (
              <p className="text-red-500 text-sm mt-1 pl-2">{passwordCheckError}</p>
            )}
          </div>

          {/* Checkboxes for Terms and Privacy Policy */}
          <div className="space-y-2 ml-1">
            <div className="flex items-center">
              <input
                type="checkbox"
                id="privacyPolicy"
                checked={privacyPolicyChecked}
                onChange={() => {
                  setPrivacyPolicyChecked(!privacyPolicyChecked);
                  if (termsError) setTermsError("");
                }}
                className="mr-2"
                required
              />
              <label htmlFor="privacyPolicy" className="text-sm text-black">
                개인정보 처리방침 동의(필수){" "}
                <a
                  href="https://subsequent-frog.super.site/29608e6e11b3800e8d73f172bb194d07"
                  className="underline"
                >
                  링크
                </a>
              </label>
            </div>

            <div className="flex items-center">
              <input
                type="checkbox"
                id="terms"
                checked={termsChecked}
                onChange={() => {
                  setTermsChecked(!termsChecked);
                  if (termsError) setTermsError("");
                }}
                className="mr-2"
                required
              />
              <label htmlFor="terms" className="text-sm text-black">
                서비스 이용약관 동의(필수){" "}
                <a
                  href="https://subsequent-frog.super.site/%EC%9D%B4%EC%9A%A9%EC%95%BD%EA%B4%80-%EC%86%8C%EB%B9%84%EC%9E%90%EC%9A%A9"
                  className="underline"
                >
                  링크
                </a>
              </label>
            </div>
            {termsError && (
              <p className="text-red-500 text-sm mt-1 pl-2">{termsError}</p>
            )}
          </div>

          {/* Submit Button */}
          <button
            type="submit"
            className={`w-full py-3 rounded-xl text-white font-bold ${
              isSubmitting
                ? "bg-gray-400 cursor-not-allowed"
                : "bg-primary hover:brightness-90 transition duration-150"
            }`}
            disabled={isSubmitting}
          >
            {isSubmitting ? "가입 중..." : "가입하기"}
          </button>
        </form>

        {/* Link to Login Page */}
        <div className="text-center mt-4">
          <button
            onClick={() => navigate("/login")}
            className="text-sm text-primary underline hover:brightness-90 transition"
          >
            로그인하기
          </button>
        </div>
      </FadeInWrapper>
    </div>
  );
};

export default SignUp;
